import { Button, FormField, Input, LoadingState, PasswordInput, toast } from '@hemmafixa/ui';
import { Link2Off } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { homePath } from './access';
import { usePasswordLink, useSetPassword } from './api';
import { authErrorMessage, newPasswordErrors } from './auth-errors';
import { AuthPage } from './auth-page';

/** The token from `#token=…`. It comes after `#` so it never reaches a server log. */
function readToken(): string | null {
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
  return token && token.length >= 20 ? token : null;
}

/** `/set-password` (new accounts) and `/reset-password` (forgot password): both open from an emailed link. */
export function PasswordLinkPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [token] = useState(readToken);
  const link = usePasswordLink(token);
  const save = useSetPassword();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<{ password?: string; repeat?: string }>({});

  // Take the token out of the address bar, so it is not left in history or shared by copying the URL
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      );
    }
  }, []);

  if (token && link.isPending) {
    return (
      <AuthPage title={t.auth.setTitle}>
        <LoadingState variant="detail" label={t.auth.checkingLink} />
      </AuthPage>
    );
  }

  if (!token || link.isError || !link.data) {
    return (
      <AuthPage title={t.auth.linkInvalidTitle}>
        <div className="grid gap-4">
          <div className="flex gap-3 text-sm text-muted-foreground">
            <Link2Off className="size-5 shrink-0" aria-hidden />
            <p>{link.isError ? t.common.errorMessage : t.auth.linkInvalidText}</p>
          </div>
          <Button asChild size="lg">
            <Link to="/forgot-password">{t.auth.requestNewLink}</Link>
          </Button>
        </div>
      </AuthPage>
    );
  }

  const info = link.data;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = newPasswordErrors(password, repeat, t);
    setErrors(found);
    if (found.password || found.repeat) return;
    save.mutate(
      { token, password },
      {
        onSuccess: ({ user }) => {
          toast.success(t.auth.passwordSaved);
          navigate(homePath(user), { replace: true });
        },
      },
    );
  };

  return (
    <AuthPage
      title={info.purpose === 'set_password' ? t.auth.setTitle : t.auth.resetTitle}
      description={fill(t.auth.forEmail, { email: info.email })}
    >
      <form className="grid gap-4" onSubmit={submit} noValidate>
        {save.isError && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm font-medium text-danger">
            {authErrorMessage(save.error, t)}
          </p>
        )}
        {/* Lets password managers save the new password under the right account */}
        <Input type="email" autoComplete="username" value={info.email} readOnly hidden />
        <FormField label={t.auth.newPassword} hint={t.auth.newPasswordHint} error={errors.password} required>
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            showLabel={t.auth.showPassword}
            hideLabel={t.auth.hidePassword}
          />
        </FormField>
        <FormField label={t.auth.repeatPassword} error={errors.repeat} required>
          <PasswordInput
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            showLabel={t.auth.showPassword}
            hideLabel={t.auth.hidePassword}
          />
        </FormField>
        <Button type="submit" size="lg" loading={save.isPending}>
          {t.auth.savePassword}
        </Button>
      </form>
    </AuthPage>
  );
}
